import * as React from "react";
import { AppColors } from "../models/General";
import { NewReservation } from "../models/Reservation";
import { useDebounce } from "../hooks/Debounce/use-debounce";
import { Heroicons } from "./Heroicon/Heroicon";
import { BaseInput } from "./baseinput/BaseInput";
import { useSettings } from "../clientApi/SettingsProvider";

type NewReservationDetail = Pick<
  Required<NewReservation>,
  "name" | "note" | "email" | "requestChannel" | "phoneNumber"
>;

export const NewReservationDetail = (
  props: {
    appColors: AppColors;
    selectDetails: (details: Partial<NewReservation>) => void;
    handleContinue?: () => void;
    active?: boolean;
  } & Partial<NewReservation>
) => {
  const [newReservation, setNewReservation] = React.useState<NewReservationDetail>({
    email: props.email || "",
    name: props.name || "",
    note: props.note || "",
    phoneNumber: props.phoneNumber || "",
    requestChannel: props.requestChannel || "",
  });

  const debouncedReservation = useDebounce(newReservation, 350);

  const [hasErrorOccured, setHasErrorOccured] = React.useState(false);

  const _handleSave = React.useCallback(() => {
    if (newReservation.name) {
      setHasErrorOccured(false);
      props.selectDetails.call(undefined, newReservation);
      if (props.handleContinue) {
        props.handleContinue.call(undefined);
      }
    } else {
      setHasErrorOccured(true);
    }
  }, [newReservation, props.handleContinue, props.selectDetails]);

  React.useEffect(() => {
    if (props.handleContinue) {
      props.selectDetails.call(undefined, debouncedReservation);
    }
  }, [debouncedReservation, props.handleContinue, props.selectDetails]);

  React.useEffect(() => {
    setNewReservation((c) => {
      let temp = c;
      if (c.email !== props.email && props.email) {
        temp = { ...temp, email: props.email };
      }
      if (c.name !== props.name && props.name) {
        temp = { ...temp, name: props.name };
      }
      if (c.note !== props.note && props.note) {
        temp = { ...temp, note: props.note };
      }
      if (c.phoneNumber !== props.phoneNumber && props.phoneNumber) {
        temp = { ...temp, phoneNumber: props.phoneNumber };
      }
      if (c.requestChannel !== props.requestChannel && props.requestChannel) {
        temp = { ...temp, requestChannel: props.requestChannel };
      }
      return temp;
    });
  }, [props.email, props.name, props.note, props.phoneNumber, props.requestChannel]);

  const _handleValue = React.useCallback(
    (key: keyof NewReservationDetail, value: NewReservationDetail[typeof key] | null) =>
      setNewReservation((res) => ({ ...res, [key]: value })),
    []
  );

  const reservationKeys: (keyof NewReservationDetail)[] = React.useMemo(
    () => ["email", "name", "note", "phoneNumber", "requestChannel"] as (keyof NewReservationDetail)[],
    []
  );

  const [changeHandler, clearHandler] = React.useMemo(() => {
    // create object that enables -> handlerObject["name"] === (value) => _handleValue("name", value);
    const handlerObject = reservationKeys.reduce(
      (obj: { [key in keyof NewReservationDetail]: (value: string | null) => void }, key) => {
        return { ...obj, [key]: (value: NewReservationDetail[typeof key] | null) => _handleValue(key, value) };
      },
      {} as { [key in keyof NewReservationDetail]: (value: string | null) => void }
    );
    const clearObject = reservationKeys.reduce((obj: { [key in keyof NewReservationDetail]: () => void }, key) => {
      return { ...obj, [key]: () => _handleValue(key, "") };
    }, {} as { [key in keyof NewReservationDetail]: () => void });

    return [handlerObject, clearObject];
  }, [_handleValue, reservationKeys]);

  const _saveOnEnter = React.useCallback(
    (ev: React.KeyboardEvent) => {
      if (ev.key === "Enter") {
        _handleSave();
      }
    },
    [_handleSave]
  );

  const { isUsingVirtualKeyboard } = useSettings();

  return (
    <div className="flex flex-1 flex-col justify-start items-start h-full w-full z-10">
      <div className={`relative flex flex-1 flex-col w-full px-2 pb-2 pt-1 min-h-0 max-h-full h-full`}>
        <div className="flex flex-col justify-start items-center w-full px-2 pb-2 space-y-1">
          <BaseInput
            label={"Name"}
            onChange={changeHandler.name}
            value={newReservation.name}
            height="h-13"
            className="flex-shrink-0"
            icon="UserIcon"
            isVirtualKeyboard={isUsingVirtualKeyboard}
          />

          <BaseInput
            icon="MailIcon"
            label={"Email"}
            onChange={changeHandler.email}
            value={newReservation.email || null}
            height="h-13"
            className="flex-shrink-0"
            isVirtualKeyboard={isUsingVirtualKeyboard}
          />

          <div className="inline-flex w-full flex-shrink-0 space-x-1">
            <BaseInput
              icon="PhoneIcon"
              label={"Telefon Nr."}
              onChange={changeHandler.phoneNumber}
              value={newReservation.phoneNumber || null}
              height="h-13"
              className="flex-shrink-0 flex-1"
              isVirtualKeyboard={isUsingVirtualKeyboard}
            />
            <a
              tabIndex={-1}
              href={`tel:${newReservation.phoneNumber}`}
              className={`w-12 h-12 rounded bg-primary-500 hover:bg-primary-600 text-white inline-flex justify-center items-center p-2 transition-all duration-200 ease-in-out`}
            >
              <Heroicons.Outline.PhoneIcon className="w-full h-full" />
            </a>
          </div>
          <BaseInput
            icon="LightBulbIcon"
            label={"Kanal"}
            onChange={changeHandler.requestChannel}
            value={newReservation.requestChannel || null}
            height="h-13"
            className="flex-shrink-0"
            isVirtualKeyboard={isUsingVirtualKeyboard}
          />

          <BaseInput
            icon="DocumentTextIcon"
            label={"Notiz"}
            onChange={changeHandler.note}
            value={newReservation.note || null}
            height="min-h-0 h-32"
            className="flex-shrink-0"
            isTextArea
            isVirtualKeyboard={isUsingVirtualKeyboard}
          />
        </div>
        <div className="flex w-full h-16 flex-shrink-0 flex-grow-0 justify-center items-center p-1 xs:p-3">
          <div
            className={`relative flex justify-center items-center w-full h-full rounded-lg bg-primary-500 shadow-md font-semibold text-lg text-white cursor-pointer `}
            onClick={_handleSave}
            onKeyDown={_saveOnEnter}
            tabIndex={0}
          >
            <span>Weiter</span>
            <span className="absolute flex h-full w-full justify-end items-center px-2">
              <Heroicons.Outline.ArrowRightIcon className="h-7 w-7" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
